import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, DatePicker, message, Space, AutoComplete, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import api from '../utils/api';
import dayjs from 'dayjs';

const Expenses = () => {
    const { t } = useTranslation();
    const { user } = useAuthStore();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [filterCategory, setFilterCategory] = useState(null);
    const [editingExpense, setEditingExpense] = useState(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: expensesData, isLoading } = useQuery('expenses', async () => {
        const response = await api.get('/expenses');
        return response.data.data;
    });

    const { data: expenseTypesData } = useQuery('expense-types', async () => {
        const response = await api.get('/expense-types');
        return response.data.data;
    });

    const { data: coursesData } = useQuery('courses', async () => {
        const response = await api.get('/courses');
        return response.data.data;
    });

    const createExpenseTypeMutation = useMutation(
        (data) => api.post('/expense-types', data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('expense-types');
            },
        }
    );

    const createMutation = useMutation(
        (data) => api.post('/expenses', data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('expenses');
                message.success(t('expenseRecordAdded'));
                setIsModalVisible(false);
                form.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToAddExpense'));
            },
        }
    );

    const updateMutation = useMutation(
        ({ id, data }) => api.put(`/expenses/${id}`, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('expenses');
                message.success(t('updatedSuccessfully'));
                setIsModalVisible(false);
                setEditingExpense(null);
                form.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToUpdate'));
            },
        }
    );

    const deleteMutation = useMutation(
        (id) => api.delete(`/expenses/${id}`),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('expenses');
                message.success(t('deletedSuccessfully'));
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToDelete'));
            },
        }
    );

    const columns = [
        { title: t('date'), dataIndex: 'date', key: 'date', render: (date) => dayjs(date).format('YYYY-MM-DD') },
        { title: t('category'), dataIndex: 'category', key: 'category' },
        { title: t('description'), dataIndex: 'description', key: 'description' },
        {
            title: t('amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount, record) => {
                const currency = record.currency || 'USD';
                const symbol = currency === 'IQD' ? 'IQD' : '$';
                return `${symbol}${amount?.toLocaleString() || '0'}`;
            }
        },
        { title: t('paymentMethod'), dataIndex: 'paymentMethod', key: 'paymentMethod' },
        ...(user?.role === 'admin' ? [{
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditingExpense(record);
                            form.setFieldsValue({
                                ...record,
                                date: record.date ? dayjs(record.date) : null,
                                relatedToCourse: record.relatedTo?.course?._id || record.relatedTo?.course
                            });
                            setIsModalVisible(true);
                        }}
                    />
                    <Popconfirm
                        title={t('areYouSureDelete')}
                        onConfirm={() => deleteMutation.mutate(record._id)}
                        okText={t('yes')}
                        cancelText={t('no')}
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            )
        }] : [])
    ];

    const handleSubmit = async (values) => {
        // Check if category is new and create it
        const existingType = expenseTypesData?.find(
            type => type.name.toLowerCase() === values.category.toLowerCase()
        );

        let expenseTypeId = existingType?._id;

        if (!existingType && values.category) {
            try {
                const result = await createExpenseTypeMutation.mutateAsync({ name: values.category });
                expenseTypeId = result.data.data._id;
            } catch (error) {
                // Type might already exist, continue anyway
            }
        }

        const payload = {
            ...values,
            date: values.date ? values.date.toISOString() : new Date().toISOString(),
        };

        // Add expenseType reference if available
        if (expenseTypeId) {
            payload.expenseType = expenseTypeId;
        }

        // Handle relatedTo field for course
        if (values.relatedToCourse) {
            payload.relatedTo = {
                model: 'Course',
                id: values.relatedToCourse,
                course: values.relatedToCourse
            };
        } else {
            // Explicitly set to None if no course selected
            payload.relatedTo = {
                model: 'None'
            };
        }

        // Remove the relatedToCourse field from payload
        delete payload.relatedToCourse;

        console.log('Submitting expense with payload:', payload);

        if (editingExpense) {
            updateMutation.mutate({ id: editingExpense._id, data: payload });
        } else {
            createMutation.mutate(payload);
        }
    };

    // Filter expenses by category
    const filteredExpenses = filterCategory
        ? expensesData?.filter(expense => expense.category === filterCategory)
        : expensesData;

    return (
        <div>
            <div className="page-header">
                <h1>{t('expenses')}</h1>
                <Space>
                    <Select
                        placeholder={t('filterByCategory')}
                        allowClear
                        style={{ width: 200 }}
                        value={filterCategory}
                        onChange={setFilterCategory}
                    >
                        {expenseTypesData?.map(type => (
                            <Select.Option key={type._id} value={type.name}>
                                {type.name}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
                        {t('addExpense')}
                    </Button>
                </Space>
            </div>
            <Table columns={columns} dataSource={filteredExpenses} rowKey="_id" loading={isLoading} />

            <Modal
                title={editingExpense ? t('editExpense') : t('addExpense')}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingExpense(null);
                    form.resetFields();
                }}
                footer={null}
                width="min(500px, 95vw)"
                centered
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item name="date" label={t('date')}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="category" label={t('category')} rules={[{ required: true, message: t('pleaseSelectCategory') }]}>
                        <AutoComplete
                            options={expenseTypesData?.map(type => ({ value: type.name })) || []}
                            placeholder={t('typeOrSelectCategory')}
                            filterOption={(inputValue, option) =>
                                option.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
                            }
                        />
                    </Form.Item>
                    <Form.Item name="description" label={t('description')}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="amount" label={t('amount')} rules={[{ required: true, message: t('pleaseEnterAmount') }]}>
                        <InputNumber style={{ width: '100%' }} min={0} />
                    </Form.Item>
                    <Form.Item name="currency" label={t('currency')} rules={[{ required: true, message: 'Please select currency' }]} initialValue="USD">
                        <Select>
                            <Select.Option value="USD">{t('USD')}</Select.Option>
                            <Select.Option value="IQD">{t('IQD')}</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="relatedToCourse" label={t('relatedCourse')}>
                        <Select
                            allowClear
                            placeholder={t('selectCourseOptional')}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.children?.toLowerCase() ?? '').includes(input.toLowerCase())
                            }
                        >
                            {coursesData?.map(course => (
                                <Select.Option key={course._id} value={course._id}>
                                    {course.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="affectsCommission"
                        label={t('affectsCommission') || 'Deduct from commission calculation'}
                        tooltip={t('affectsCommissionTooltip') || 'If enabled, this expense will be deducted from course income before calculating staff commission'}
                        valuePropName="checked"
                        initialValue={false}
                    >
                        <Select>
                            <Select.Option value={true}>{t('yes') || 'Yes'}</Select.Option>
                            <Select.Option value={false}>{t('no') || 'No'}</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="paymentMethod" label={t('paymentMethod')}>
                        <Select>
                            <Select.Option value="cash">{t('cash')}</Select.Option>
                            <Select.Option value="bank">{t('bank')}</Select.Option>
                            <Select.Option value="other">{t('other')}</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={createMutation.isLoading || updateMutation.isLoading}>
                                {editingExpense ? t('update') : t('create')}
                            </Button>
                            <Button onClick={() => {
                                setIsModalVisible(false);
                                setEditingExpense(null);
                                form.resetFields();
                            }}>{t('cancel')}</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Expenses;
